import { PortalActionMenu } from "components/PortalActionMenu";
import { Badge } from "components/ui/Badge/Badge";
import {
  type ReplyEmailItem,
  type ReplyResponseStatus,
  replyStatusBadgeColor,
  replyStatusLabel,
} from "hooks/use-email-replies-api";
import { cn } from "lib/utils";
import { MessageSquare, MoreVertical, Paperclip } from "lucide-react";
import { type FC, useMemo } from "react";
import { ReplyParentPill } from "routes/emails/EmailRepliesPage/components/ReplyParentPill";
import { buildStatusMenuItems } from "routes/emails/EmailRepliesPage/lib/build-status-menu-items";
import { timeAgo } from "utils/relative-time";

export type ReplyListItemProps = {
  /** スレッド内の代表リプライ (受信時刻最新) */
  reply: ReplyEmailItem;
  /** スレッド内のリプライ件数. 2 以上のときに件数バッジを表示 */
  threadCount: number;
  /**
   * 起点ラベル (例: シリーズ「アナスタシアさん紹介」, 提案「Power BI…」).
   * null の場合は表示しない (parent_type=standalone 等).
   */
  originLabel: string | null;
  /**
   * スレッド全体の集約ステータス.
   * undefined の場合は reply.responseStatus にフォールバック.
   */
  aggregateStatus?: ReplyResponseStatus;
  selected: boolean;
  checked: boolean;
  onSelect: (id: string) => void;
  onToggleChecked: (id: string) => void;
  /** 三点リーダーからのステータス変更 */
  onChangeStatus: (id: string, status: ReplyResponseStatus) => void;
};

/**
 * 一覧 1 行 = 1 スレッド. ヘッダ + 起点ラベル + 件名 + 本文プレビュー + 三点リーダー.
 *
 * 表示構造:
 *   1 行目: status dot + parentPill + 送信元名 + 件数バッジ + 受信時刻 + 三点リーダー
 *   2 行目: 起点 (シリーズ/配信/提案) — そのスレッドが「何の返信か」 を一目で識別
 *   3 行目: 最新リプライ件名 (Re: …)
 *   4 行目: 最新本文 1 行プレビュー
 *   5 行目: パイプライン情報 (タレント/案件) があれば表示
 *
 * @param {ReplyListItemProps} props スレッドグループと UI 状態
 * @return {JSX.Element} 1 行
 */
export const ReplyListItem: FC<ReplyListItemProps> = ({
  reply,
  threadCount,
  originLabel,
  aggregateStatus,
  selected,
  checked,
  onSelect,
  onToggleChecked,
  onChangeStatus,
}) => {
  const bodyPreview = reply.bodyText?.split("\n")[0] ?? "";
  const pipelineLabel = [reply.pipelineTalentName, reply.pipelineMatterTitle]
    .filter(Boolean)
    .join(" / ");
  const dotStatus = aggregateStatus ?? reply.responseStatus;
  const isMultiMessage = threadCount > 1;

  const menuItems = useMemo(
    () =>
      buildStatusMenuItems(reply.responseStatus, status =>
        onChangeStatus(reply.id, status),
      ),
    [reply.id, reply.responseStatus, onChangeStatus],
  );

  return (
    <li
      className={cn(
        "px-3 py-2 cursor-pointer hover:bg-slate-50 border-b border-slate-100",
        selected && "bg-violet-50",
      )}
    >
      <div className="flex items-start gap-2">
        <input
          type="checkbox"
          className="checkbox checkbox-sm mt-0.5"
          checked={checked}
          onChange={() => onToggleChecked(reply.id)}
          onClick={e => e.stopPropagation()}
          aria-label="リプライを選択"
        />
        <button
          type="button"
          className="flex-1 min-w-0 text-left"
          onClick={() => onSelect(reply.id)}
        >
          <div className="flex items-center gap-2">
            <Badge color={replyStatusBadgeColor(dotStatus)} size="xs">
              {replyStatusLabel(dotStatus)}
            </Badge>
            <ReplyParentPill type={reply.parentType} compact />
            <span className="text-sm font-medium truncate text-base-content">
              {reply.fromName || reply.fromEmail}
            </span>
            {isMultiMessage && (
              <span
                className="inline-flex items-center gap-0.5 text-[10px] text-base-content/60 shrink-0"
                title={`${threadCount} 件のメッセージ`}
              >
                <MessageSquare size={10} aria-hidden />
                {threadCount}
              </span>
            )}
            <span className="ml-auto text-xs text-base-content/50 shrink-0">
              {timeAgo(reply.receivedAt)}
            </span>
          </div>
          {originLabel && (
            <div className="text-xs text-violet-700 truncate mt-0.5">
              {originLabel}
            </div>
          )}
          <div className="text-sm truncate mt-0.5 text-base-content">
            {reply.subject || "(件名なし)"}
          </div>
          {bodyPreview && (
            <div className="text-xs text-base-content/60 truncate mt-0.5">
              {bodyPreview}
            </div>
          )}
          {pipelineLabel && (
            <div className="text-[10px] text-violet-700 mt-0.5 truncate flex items-center gap-1">
              <Paperclip size={10} aria-hidden />
              <span className="truncate">{pipelineLabel}</span>
            </div>
          )}
        </button>
        <div
          className="shrink-0"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
          role="presentation"
        >
          <PortalActionMenu
            items={menuItems}
            buttonLabel={<MoreVertical size={14} aria-label="操作メニュー" />}
          />
        </div>
      </div>
    </li>
  );
};
